import { isEmpty } from 'lodash';
import { ClipboardEvent, useEffect, useState } from 'react';
import { Box, IconButton, LoaderOverlay, Stack, TextField } from '@common-components';
import { ArrowForward } from '@icons';
import { AIMessageType } from 'enums';
import { useCtrlEnterSubmitButton, useScrollToBottomOnContentChange } from 'hooks';
import { messages } from 'i18n';
import { AIMessage } from 'types';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import { AIGeneratedEmptyState } from './AIGeneratedEmptyState';
import { AIPromptMessage } from './AIPromptMessage';
import { AIResponseMessage } from './AIResponseMessage';
import { AIResponseMessageSkeleton } from './AIResponseMessageSkeleton';
import { FeedbackFormData } from './MessageActions/FeedbackForm/form-utils';

export function AIGeneratedContent({
  withFileLinks,
  isRichTextResponse,
  initialMessages,
  maxWidth,
  withEmptyState,
  addFollowupMessageCustomHandler,
  initialQuestion = '',
  autoRun,
}: {
  withFileLinks: boolean;
  isRichTextResponse: boolean;
  initialMessages?: AIMessage[];
  maxWidth?: number;
  withEmptyState?: boolean;
  addFollowupMessageCustomHandler?: (message: string) => Promise<void>;
  initialQuestion?: string;
  autoRun?: boolean;
}) {
  const {
    feature,
    messages: aiMessages,
    isLoading,
    isContentStreaming,
    addFollowupMessage,
    createThreadFromInitialMessages,
    sendAIThread,
    isRegenerating,
  } = useAIContext();
  const belowContentRef = useScrollToBottomOnContentChange(aiMessages);
  const [followupQuestion, setFollowupQuestion] = useState(initialQuestion);

  const { buttonRef, handleKeyDown } = useCtrlEnterSubmitButton();

  const onMessageSent = async () => {
    if (initialMessages?.length && initialMessages.length === aiMessages.length) {
      await createThreadFromInitialMessages(followupQuestion);
    } else if (addFollowupMessageCustomHandler) {
      await addFollowupMessageCustomHandler(followupQuestion);
    } else {
      await addFollowupMessage(followupQuestion);
    }
    setFollowupQuestion('');
  };

  // Auto-execute the initial message when autoRun is enabled
  useEffect(() => {
    if (autoRun && followupQuestion === initialQuestion && !!initialQuestion) {
      onMessageSent();
    }
    // remove onMessageSent from dependencies to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, followupQuestion, initialQuestion]);

  if (isRegenerating && !isContentStreaming) {
    return <LoaderOverlay />;
  }

  if (isEmpty(aiMessages) && withEmptyState) {
    return <AIGeneratedEmptyState />;
  }

  const onFeedbackSubmit = async (data: FeedbackFormData, index: number) => {
    await sendAIThread({
      feedback: data.feedback,
      messages: aiMessages.slice(0, index + 1),
    });
  };

  const handlePasteAsPlainText = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!isEmpty(followupQuestion)) {
      return;
    }

    event.preventDefault(); // Prevent the default paste action

    // This is a hack to get the plain text from the clipboard
    const clipboardHtmlData = event.clipboardData.getData('text/html');
    if (isEmpty(clipboardHtmlData)) {
      setFollowupQuestion(event.clipboardData.getData('text/plain'));
      return;
    }
    const tempDiv = document.createElement('div');

    // This part aims to replace <br> and <p> tags with new lines (otherwise we lose them when extracting the inner text
    tempDiv.innerHTML = clipboardHtmlData
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      .replace(/<\/p>/gi, '\n\n');

    const plainText = tempDiv.textContent || tempDiv.innerText || '';

    setFollowupQuestion(plainText);
  };

  return (
    <Stack flex={1} direction="column" overflow="hidden">
      <Stack flex={1} direction="column" p={1} pb={0} overflow="auto">
        {isLoading && isEmpty(aiMessages) && <AIResponseMessageSkeleton />}
        {aiMessages.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} display="grid">
            {message.type === AIMessageType.Prompt ? (
              <AIPromptMessage message={message} maxWidth={maxWidth} />
            ) : (
              <AIResponseMessage
                message={message}
                onFeedbackSubmit={onFeedbackSubmit}
                index={index}
                withFileLinks={withFileLinks}
                isRichTextResponse={isRichTextResponse}
              />
            )}
          </Box>
        ))}
        <Box ref={belowContentRef} />
      </Stack>
      <Stack direction="row" mt="auto" gap={1} pt={2} pb={1} bgcolor="common.white" mx={1}>
        <TextField
          variant="outlined"
          maxRows={10}
          multiline
          sx={{ marginTop: 'auto', flex: 1 }}
          placeholder={messages.submissionWorkspace.aiGeneratedContentPopup.askAboutThis(
            messages.submissionWorkspace.aiGeneratedContentPopup.featureToName[feature],
          )}
          value={followupQuestion}
          onChange={({ target }) => {
            setFollowupQuestion(target.value);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePasteAsPlainText}
          disabled={isLoading || isContentStreaming}
        />
        <Box alignSelf="end" pb={1}>
          <IconButton
            ref={buttonRef}
            disabled={isLoading || isContentStreaming || followupQuestion === ''}
            icon={ArrowForward}
            size="medium"
            loading={isLoading}
            onClick={onMessageSent}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
